import React, { useEffect, useMemo, useState, useRef } from "react";
import { Card, Button, Image, OverlayTrigger, Tooltip } from "react-bootstrap";
import {
  IoCheckmarkCircleOutline,
  IoAlertCircleOutline,
} from "react-icons/io5";
import { useSelector, useDispatch } from "react-redux";
import { api_paths } from "../config/api.paths";
import API from "../config/axios";
import { SET_USER } from "../redux/user";
import EditProfileModal from "../components/member/EditProfileModal";
import editIcon from "../assets/images/icons/edit.svg";
// import closeIcon from "../assets/images/icons/close.svg";
import EditMobileModal from "../components/member/EditMobileModal";
import OtpModal from "../components/auth/OtpModal";
import { toastErrorMessage, toastSuccessMessage } from "../utils/utils";
import { SET_LOADING } from "../redux/user";
import { useNavigate } from "react-router-dom";
import LeftArrow from "../assets/images/committee/ArrowLeft.svg";
import ProfilePhotoCropper from "../components/member/ProfilePhotoCropper";
// import ConfirmDeleteModal from "../components/common/ConfirmDeleteModal";
import { formatToUSDate } from "../utils/dateFormat";
import AddProfilePhotoModal from "../components/common/AddProfilePhotoModal";
import qrcodeImg from "../assets/images/icons/qrcode.svg";
import MemberCardModal from "../components/member/MemberCardModal";

export default function ProfilePage() {
  const dispatch = useDispatch();

  const reduxUser = useSelector((state) => state.USER_DATA.user);
  const [user, setUser] = useState(reduxUser);
  const { loading } = useSelector((state) => state.USER_DATA);
  const [showEditProfileModal, setShowEditProfileModal] = useState(false);
  const [showUpdateMobileModal, setShowUpdateMobileModal] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpId, setOtpId] = useState(null);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [savingPhoto, setSavingPhoto] = useState(false);
  const [showMemberCard, setShowMemberCard] = useState(false);
  // const [showDeleteModal, setShowDeleteModal] = useState(false);
  const IMAGE_BASEURL = process.env.REACT_APP_IMAGE_BASEURL || "";
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showAddPhotoPrompt, setShowAddPhotoPrompt] = useState(false);
  // const [hasPrompted, setHasPrompted] = useState(false);

  const [resolvingAddress, setResolvingAddress] = useState(false);
  const fetchUser = async () => {
    try {
      dispatch(SET_LOADING(true));
      const response = await API.get(api_paths.member.GET_USER);
      if (response.status === 200) {
        const data = response.data?.payload?.result;
        setUser(data);
        dispatch(SET_USER(data));
        if (data?.address) {
          await resolveAddressNames(data.address);
        }
      }
    } catch (error) {
      console.log("Error fetching user:", error);
    } finally {
      dispatch(SET_LOADING(false));
    }
  };

  const resolveAddressNames = async (address) => {
    if (!address) return;
    setResolvingAddress(true);
    try {
      const updatedAddress = { ...address };
      let hasChanges = false;

      // Resolve Country
      if (address.country && typeof address.country === "string") {
        const countryRes = await API.get(
          `${api_paths.address.GET_COUNTRY}?pagination=false`,
        );
        const country = countryRes.data?.payload?.data?.find(
          (c) => c._id === address.country,
        );
        if (country) {
          updatedAddress.country = country;
          hasChanges = true;
        }
      }

      // Resolve State
      const countryId =
        typeof updatedAddress.country === "string"
          ? updatedAddress.country
          : updatedAddress.country?._id;
      if (address.state && typeof address.state === "string" && countryId) {
        const stateRes = await API.get(
          `${api_paths.address.GET_STATE}?country_id=${countryId}&pagination=false`,
        );
        const state = stateRes.data?.payload?.data?.find(
          (s) => s._id === address.state,
        );
        if (state) {
          updatedAddress.state = state;
          hasChanges = true;
        }
      }

      // Resolve City
      const stateId =
        typeof updatedAddress.state === "string"
          ? updatedAddress.state
          : updatedAddress.state?._id;
      if (address.city && typeof address.city === "string" && stateId) {
        const cityRes = await API.get(
          `${api_paths.address.GET_CITY}?state_id=${stateId}&pagination=false`,
        );
        const city = cityRes.data?.payload?.data?.find(
          (c) => c._id === address.city,
        );
        if (city) {
          updatedAddress.city = city;
          hasChanges = true;
        }
      }

      if (hasChanges) {
        setUser((prev) => ({ ...prev, address: updatedAddress }));
      }
    } catch (error) {
      console.error("Error resolving address names:", error);
    } finally {
      setResolvingAddress(false);
    }
  };

  useEffect(() => {
    if (user?.address) {
      resolveAddressNames(user.address);
    }
  }, [user?.address]);

  const profileImage = useMemo(() => {
    const raw =
      user?.profile_picture || user?.profile_picture_path || user?.image || "";
    if (!raw) return null;
    if (raw.startsWith("http")) return raw;
    return `${IMAGE_BASEURL}${raw}`;
  }, [user, IMAGE_BASEURL]);

  const initials = useMemo(() => {
    const first = user?.first_name?.[0] || "";
    const last = user?.last_name?.[0] || "";
    return `${first}${last}`.toUpperCase() || "?";
  }, [user]);

  const uploadProfilePhoto = async (file) => {
    if (!file) return;
    try {
      setSavingPhoto(true);
      dispatch(SET_LOADING(true));

      const formData = new FormData();
      formData.append("profile_picture", file);

      await API.put(api_paths.member.EDIT_PROFILE, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toastSuccessMessage("Profile photo updated");
      await fetchUser();

      setSelectedImage(null);
      setShowPhotoModal(false);
    } catch (error) {
      toastErrorMessage(
        error.response?.data?.message || "Failed to update profile photo",
      );
    } finally {
      setSavingPhoto(false);
      dispatch(SET_LOADING(false));
    }
  };

  // const deleteProfilePhoto = async () => {
  //   try {
  //     setSavingPhoto(true);
  //     dispatch(SET_LOADING(true));
  //     await API.delete(api_paths.member.DELETE_PROFILE);
  //     toastSuccessMessage("Profile photo removed");
  //     await fetchUser();
  //     setShowPhotoModal(false);
  //     setShowDeleteModal(false);
  //   } catch (error) {
  //     toastErrorMessage(
  //       error.response?.data?.message || "Failed to delete profile photo"
  //     );
  //   } finally {
  //     setSavingPhoto(false);
  //     dispatch(SET_LOADING(false));
  //   }
  // };

  useEffect(() => {
    if (!reduxUser || !reduxUser._id) {
      fetchUser();
    }
    // eslint-disable-next-line
  }, []);
  useEffect(() => {
    if (reduxUser) {
      setUser(reduxUser);
    }
  }, [reduxUser]);

  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    const lastPromptDate = localStorage.getItem("last_profile_photo_prompt");

    if (user && !profileImage && !loading && lastPromptDate !== today) {
      setShowAddPhotoPrompt(true);
      localStorage.setItem("last_profile_photo_prompt", today);
    }
  }, [user, profileImage, loading]);

  const handleVerifyOtp = async (otp, otp_id) => {
    try {
      dispatch(SET_LOADING(true));
      const response = await API.post(api_paths.member.VERIFY_OTP, {
        otp,
        otp_id,
      });

      if (response.status === 201) {
        toastSuccessMessage("Mobile number updated successfully");

        setShowOtpModal(false);
        await fetchUser();

        setUser((prev) => ({
          ...prev,
          phone: response.data.payload.result.phone,
          country_code: response.data.payload.result.country_code,
        }));
      }
    } catch (error) {
      toastErrorMessage(
        error.response?.data?.message || "Failed to verify OTP",
      );
    } finally {
      dispatch(SET_LOADING(false));
    }
  };

  const handleResendOtp = async (otp_id) => {
    try {
      dispatch(SET_LOADING(true));
      const response = await API.post(api_paths.member.RESEND_OTP, {
        otp_id,
      });

      if (response.status === 200) {
        toastSuccessMessage("OTP resent successfully");
      }
    } catch (error) {
      toastErrorMessage(
        error.response?.data?.message || "Failed to resend OTP",
      );
    } finally {
      dispatch(SET_LOADING(false));
    }
  };

  const handleResendVerification = async () => {
    try {
      dispatch(SET_LOADING(true));
      const response = await API.post(
        api_paths.member.RESEND_VERIFICATION_EMAIL,
      );
      if (response.status === 200) {
        toastSuccessMessage(
          response.data?.message || "Verification email sent successfully!",
        );
      }
    } catch (error) {
      toastErrorMessage(
        error?.response?.data?.message || "Failed to resend verification email",
      );
    } finally {
      dispatch(SET_LOADING(false));
    }
  };

  if (!user) return <div className="text-center">Loading...</div>;

  return (
    <div className="container py-5">
      <div className="d-flex mb-2 justify-content-between align-items-start align-items-sm-center gap-2 w-100">
        <div className="d-flex align-items-center">
          <img
            className="cursor-pointer flex-shrink-0"
            src={LeftArrow}
            alt="back-arrow"
            onClick={() => navigate(-1)}
          />
          <h2 className="fw-bold profile-info-heading ms-3 mb-0">
            Welcome, {user.first_name}{" "}
            {user.middle_name ? user.middle_name + " " : ""}
            {user.last_name}!
          </h2>
        </div>
        <button
          className="qrcode-icon-btn flex-shrink-0 mt-1 mt-sm-0"
          onClick={() => setShowMemberCard(true)}
          title="View Membership Card"
        >
          <img src={qrcodeImg} alt="QR Code" className="qrcode-icon-img" />
        </button>
      </div>
      {!user.is_verify_email && (
        <div className="f-16 danger-text fw-semibold">
          Note: Email verification pending. Please verify your email.
        </div>
      )}

      <div className="row mt-3">
        {/* LEFT SIDE – PROFILE INFO */}
        <div className="col-lg-8 mb-4">
          <Card className="p-4 profile-info-card">
            <h5 className="fw-semibold mb-4">Profile Information</h5>

            <div className="row flex-column-reverse flex-lg-row">
              {/* LEFT SIDE – DETAILS (WIDTH UNCHANGED) */}
              <div className="col-lg-9 col-xl-8">
                <div className="mb-3 field-line">
                  <label className="form-label">Name</label>
                  <div className="fw-semibold">
                    {user.first_name} {user.middle_name} {user.last_name}
                  </div>
                </div>

                <div className="mb-3 field-line">
                  <label className="form-label">Email</label>
                  <div className="fw-semibold d-flex align-items-center email-row">
                    {user.email}
                    <div>
                      {user.is_verify_email ? (
                        <IoCheckmarkCircleOutline
                          className="text-success ms-2"
                          title="Verified"
                        />
                      ) : (
                        <>
                          <IoAlertCircleOutline
                            className="text-danger ms-md-2"
                            title="Not Verified"
                          />
                          <Button
                            variant="link"
                            className="p-0 ms-2 text-decoration-none small text-primary"
                            onClick={handleResendVerification}
                            style={{ fontSize: "0.8rem" }}
                          >
                            Resend Verification
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mb-3 field-line position-relative">
                  <label className="form-label mb-1">Mobile</label>
                  <div className="fw-semibold pe-4">
                    {user.country_code} {user.phone}
                  </div>

                  <img
                    src={editIcon}
                    alt="edit"
                    className="edit-icon"
                    onClick={() => setShowUpdateMobileModal(true)}
                  />
                </div>

                <div className="mb-3 field-line">
                  <label className="form-label">Date Of Birth</label>
                  <div className="fw-semibold">
                    {user.date_of_birth
                      ? formatToUSDate(user.date_of_birth)
                      : "—"}
                  </div>
                </div>

                <div className="mb-3 field-line">
                  <label className="form-label">Gender</label>
                  <div className="fw-semibold text-capitalize">
                    {user.gender || "—"}
                  </div>
                </div>

                <div className="mb-3 field-line">
                  <label className="form-label">Marital Status</label>
                  <div className="fw-semibold text-capitalize">
                    {user.marital_status || "—"}
                  </div>
                </div>

                <div className="mb-3 field-line">
                  <label className="form-label">Address</label>
                  <div className="fw-semibold">
                    {resolvingAddress ? (
                      <span className="text-muted small fw-normal">
                        Loading address...
                      </span>
                    ) : user.address?.line1 ? (
                      <>
                        {user.address?.line1}
                        {user.address?.line2 ? `, ${user.address?.line2}` : ""}
                        <br />
                        {user.address?.city?.name || ""}
                        {user.address?.city?.name && user.address?.state?.name
                          ? ", "
                          : ""}
                        {user.address?.state?.name || ""} {user.address?.zip}
                        <br />
                        {user.address?.country?.name || ""}
                      </>
                    ) : (
                      "—"
                    )}
                  </div>
                </div>

                <Button
                  className="btn-orange-lg mt-3"
                  onClick={() => setShowEditProfileModal(true)}
                >
                  Edit Profile
                </Button>
              </div>

              {/* RIGHT SIDE – PROFILE PHOTO (SEPARATE COLUMN) */}
              <div className="col-lg-3 col-xl-4 d-flex justify-content-center">
                <div
                  style={{
                    width: 100,
                    height: 100,
                    position: "relative",
                  }}
                >
                  {profileImage ? (
                    <Image
                      src={profileImage}
                      roundedCircle
                      width={"100%"}
                      height={"100%"}
                      style={{
                        objectFit: "cover",
                        border: "1px solid #dee2e6",
                      }}
                      alt="Profile"
                    />
                  ) : (
                    <div
                      style={{
                        width: 100,
                        height: 100,
                        borderRadius: "50%",
                        background: "#f2f2f2",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 700,
                        color: "#555",
                      }}
                    >
                      {initials}
                    </div>
                  )}

                  {/* EDIT PHOTO */}
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>Edit photo</Tooltip>}
                  >
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      style={{
                        position: "absolute",
                        right: 0,
                        bottom: 0,
                        // transform: "translate(25%, 25%)",
                        background: "#FEF7EA",
                        border: "none",
                        borderRadius: "50%",
                        width: 28,
                        height: 28,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        zIndex: 2,
                      }}
                    >
                      <img src={editIcon} alt="edit" width={16} height={16} />
                    </button>
                  </OverlayTrigger>

                  {/* DELETE PHOTO */}
                  {/* {profileImage && (
                    <button
                      type="button"
                      onClick={() => setShowDeleteModal(true)}
                      style={{
                        position: "absolute",
                        right: "3px",
                        top: "5px",
                        // transform: "translate(-25%, -25%)",
                        background: "var(--white)",
                        border: "1px solid #ED0505",
                        borderRadius: "50%",
                        width: 20,
                        height: 20,
                        color: "#ED0505",
                        fontSize: 15,
                        cursor: "pointer",
                        zIndex: 2,
                      }}
                    >
                      <img
                        src={closeIcon}
                        alt="delete"
                        width={12}
                        height={12}
                        className="d-flex justify-content-center align-items-center w-100 h-100"
                        style={{ padding: "5px" }}
                      />
                    </button>
                  )} */}
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* RIGHT SIDE – MEMBERSHIP SECTION */}
        <div className="col-lg-4">
          {/* CASE 1: ACTIVE MEMBERSHIP */}
          {new Date(user.membership_expiry_date) > new Date() &&
            user.membership_id && (
              <Card className="profile-membership-card mb-4">
                <div
                  style={{
                    background: "var(--orange)",
                    padding: "12px 0",
                    color: "var(--white)",
                    fontWeight: 600,
                    textAlign: "center",
                    fontSize: "16px",
                  }}
                >
                  Membership
                </div>

                <div
                  style={{
                    background: "#EDA92B1A",
                    padding: "24px",
                    textAlign: "center",
                    fontSize: "14px",
                    color: "var(--text-2)",
                  }}
                >
                  <p className="mb-0 fw-500">
                    Expiry Date: {formatToUSDate(user.membership_expiry_date)}
                  </p>
                </div>
              </Card>
            )}

          {/* CASE 2: EXPIRED MEMBERSHIP */}
          {new Date(user.membership_expiry_date) < new Date() &&
            user.membership_id && (
              <Card className="profile-membership-card mb-4">
                <div
                  style={{
                    background: "#E8A033",
                    padding: "12px 0",
                    color: "white",
                    fontWeight: 600,
                    textAlign: "center",
                    fontSize: "16px",
                  }}
                >
                  Membership Expired
                </div>

                <div
                  style={{
                    background: "#EDA92B1A",
                    padding: "24px",
                    textAlign: "center",
                    fontSize: "14px",
                    color: "var(--text-2)",
                  }}
                >
                  <p className="mb-0 fw-500">
                    Expiry Date: {formatToUSDate(user.membership_expiry_date)}
                  </p>

                  <Button
                    className="btn-orange mt-3 w-100"
                    onClick={() => {
                      navigate("/membership");
                    }}
                  >
                    Renew Membership
                  </Button>
                </div>
              </Card>
            )}

          {/* CASE 3: NO MEMBERSHIP */}
          {!user.membership_id && (
            <Card className="profile-membership-card mb-4">
              <div
                style={{
                  background: "#E8A033",
                  padding: "12px 0",
                  color: "white",
                  fontWeight: 600,
                  textAlign: "center",
                  fontSize: "16px",
                }}
              >
                Membership
              </div>

              <div
                style={{
                  background: "#EDA92B1A",
                  padding: "24px",
                  textAlign: "center",
                  fontSize: "14px",
                  color: "var(--text-2)",
                }}
              >
                <p className="mb-0 fw-500">You don't have active membership.</p>

                <Button
                  className="btn-orange mt-3 w-100"
                  onClick={() => {
                    navigate("/membership");
                  }}
                >
                  Go to Membership
                </Button>
              </div>
            </Card>
          )}
        </div>
      </div>

      <EditProfileModal
        key={user?._id}
        show={showEditProfileModal}
        onHide={() => setShowEditProfileModal(false)}
        editData={user}
        refreshList={fetchUser}
      />

      <EditMobileModal
        show={showUpdateMobileModal}
        onHide={() => setShowUpdateMobileModal(false)}
        editData={user}
        refreshList={fetchUser}
        onOtpOpen={(id) => {
          setOtpId(id);
          setShowOtpModal(true);
        }}
      />

      <OtpModal
        isOpen={showOtpModal}
        onClose={() => setShowOtpModal(false)}
        onVerify={handleVerifyOtp}
        onResend={handleResendOtp}
        otp_id={otpId}
      />

      <ProfilePhotoCropper
        show={showPhotoModal}
        onHide={() => {
          setSelectedImage(null);
          setShowPhotoModal(false);
        }}
        onSave={uploadProfilePhoto}
        initialImage={selectedImage || profileImage}
        saving={savingPhoto}
      />
      {/* <ConfirmDeleteModal
        show={showDeleteModal}
        onCancel={() => setShowDeleteModal(false)}
        onConfirm={deleteProfilePhoto}
        title="Remove profile photo?"
        message="This will remove your current profile photo."
        confirmText="Delete"
      /> */}

      <AddProfilePhotoModal
        show={showAddPhotoPrompt}
        onHide={() => setShowAddPhotoPrompt(false)}
        onPickPhoto={() => {
          setShowAddPhotoPrompt(false);
          fileInputRef.current?.click();
        }}
      />

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (!file) return;

          const reader = new FileReader();
          reader.onload = (ev) => {
            setSelectedImage(ev.target.result);
            setShowPhotoModal(true); // open crop modal AFTER file select
          };
          reader.readAsDataURL(file);

          e.target.value = ""; // allow reselect same image
        }}
      />
      <MemberCardModal
        show={showMemberCard}
        onHide={() => setShowMemberCard(false)}
        user={user}
      />
    </div>
  );
}
